import {
  useMutation,
  type UseMutationResult,
} from '@tanstack/react-query';

import type {
  IdentityClient,
  MagicLinkConsumeResult,
  MagicLinkRequestResult,
} from '@/api/identity-client';
import { useIdentityClient } from '@/api/identity-client-context';

// =============================================================================
// use-identity.ts — Hooks TanStack Query de l'onboarding fournisseur (via double).
// =============================================================================
// Demande de magic-link (POST) et consommation (POST) sont des MUTATIONS (effets
// de bord serveur : émission DE.MagicLinkIssued, ouverture de session). Le client
// effectif vient de l'IdentityClientProvider (doublable). Aucune règle métier
// ici : l'erreur API (TOKEN_EXPIRED, RATE_LIMITED, …) remonte telle quelle pour
// affichage et remédiation (l'autorité de validation est l'API).

const IDENTITY_KEY = ['identity'] as const;

/**
 * POST activation initiale du credential fournisseur (S0-ACTIVATION, F1/F2). Usage unique strict :
 * consomme l'ActivationToken ET ouvre la session (l'API pose les cookies). MUTATION (effet de bord
 * serveur). L'erreur API (TOKEN_INVALID/EXPIRED/CONSUMED) remonte telle quelle pour affichage.
 */
export function useActivateSupplier(): UseMutationResult<void, Error, string> {
  const client: IdentityClient = useIdentityClient();
  return useMutation<void, Error, string>({
    mutationKey: [...IDENTITY_KEY, 'activate'],
    mutationFn: (token: string) => client.activate(token),
  });
}

/** POST demande de magic-link — accusé d'envoi (jamais le lien en clair). */
export function useRequestMagicLink(): UseMutationResult<
  MagicLinkRequestResult,
  Error,
  string
> {
  const client: IdentityClient = useIdentityClient();
  return useMutation<MagicLinkRequestResult, Error, string>({
    mutationKey: [...IDENTITY_KEY, 'request-magic-link'],
    mutationFn: (email: string) => client.requestMagicLink(email),
  });
}

/** POST consommation de magic-link — usage unique strict (IT-IDENTITY-02). */
export function useConsumeMagicLink(): UseMutationResult<
  MagicLinkConsumeResult,
  Error,
  string
> {
  const client: IdentityClient = useIdentityClient();
  return useMutation<MagicLinkConsumeResult, Error, string>({
    mutationKey: [...IDENTITY_KEY, 'consume-magic-link'],
    mutationFn: (token: string) => client.consumeMagicLink(token),
  });
}
